import {Clone} from "js-vextensions";
import {RunInAction, observer_mgl} from "mobx-graphlink";
import React from "react";
import {VMenuItem, VMenuStub} from "react-vmenu";
import {ES, RunInAction_Set} from "web-vcore";
import {AddMapObject, UpdateMapObject} from "../../../../Server/Commands/MapObjectCommands.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../../Store/main/maps/@MapState.js";
import {liveSkin} from "../../../../Utils/Styles/StyleManager.js";
import {ShowMessageBox} from "react-vmessagebox";
import {DeleteMapObject} from "../../../../Server/Commands/MapObjectCommands.js";
import {ACTSetCopiedData, CopiedData} from "../../../../Store/main/general/CopiedData.js";
import {store} from "../../../../Store/index.js";
import {GetMapObject} from "../../../../Server/DB/mapObjects.js";
import {GetObjAncestors, ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {RotateOffsetByRotation, TransformWorldOffsetToObjLocalPoint_UsingRotationOnly} from "../../../../Utils/ThreeJS/SpaceUtils.js";
import {Vec2, Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {ShowSubtreeExportDialog} from "../ObjectComps/@Shared/SubtreeExportDialog.js";

import {MapSurface_current} from "../MapSurface.js";

export type ObjectEntryUI_Menu_Props = {obj: MapObject, map: DMap, mapState: MapState};

export const ObjectEntryUI_MenuStub = observer_mgl((props: ObjectEntryUI_Menu_Props & {delayEventHandler?: boolean})=>{
	const {delayEventHandler, ...rest} = props;
	return (
		<VMenuStub delayEventHandler={delayEventHandler ?? false} preOpen={e=>!e.handled}>
			<ObjectEntryUI_Menu {...rest}/>
		</VMenuStub>
	);
});
const ObjectEntryUI_Menu = observer_mgl((props: ObjectEntryUI_Menu_Props)=>{
	const {map, obj, mapState} = props;
	const obj_posWorld = ResolveObjPosInWorld(obj);
	const copiedData_objects = store.main.copiedData?.mapObjects;
	const copiedObjects = copiedData_objects?.ids.map(id=>GetMapObject(id, map.id)).filter(a=>a != null) as MapObject[] | null;
	const copiedObjects_worldPositions = copiedObjects?.map(a=>ResolveObjPosInWorld(a));
	//const copiedObject_oldParent = GetMapObject(copiedObject?.parent);
	const objInSelection = mapState.selectedObjects.includes(obj.id);
	const objAncestors = GetObjAncestors(obj);

	const headerStyle = ES(liveSkin.Style_VMenuItem(), {opacity: 1});
	return <>
		<VMenuItem text="Add child (empty)" style={headerStyle} onClick={async()=>{
			const newObject = new MapObject({
				map: map.id,
				parent: obj.id,
				name: `New (${new Date().toLocaleTimeString("sv")})`,
				position: Vec3.zero,
			});
			const {id: newObjId} = await new AddMapObject({entry: newObject}).RunOnServer();
			RunInAction_Set(()=>{
				mapState.selectedObjects = [newObjId];
				mapState.activeObject = newObjId;
			});
		}}/>
		<VMenuItem text={[`Cut`, mapState.selectedObjects?.length].filter(a=>a).join(" ")} style={headerStyle} enabled={objInSelection} onClick={()=>{
			ACTSetCopiedData(new CopiedData({mapObjects: {ids: [...mapState.selectedObjects], mode: "cut"}}));
		}}/>
		<VMenuItem text={[`Copy`, mapState.selectedObjects?.length].filter(a=>a).join(" ")} style={headerStyle} enabled={objInSelection} onClick={()=>{
			ACTSetCopiedData(new CopiedData({mapObjects: {ids: [...mapState.selectedObjects], mode: "copy"}}));
		}}/>
		<VMenuItem text={[`Paste`, copiedObjects?.length].filter(a=>a).join(" ")} enabled={!!(obj_posWorld && copiedObjects && copiedObjects_worldPositions)} style={headerStyle} onClick={async()=>{
			if (!(obj_posWorld && copiedObjects && copiedObjects_worldPositions)) return;
			if (copiedData_objects?.mode == "cut") {
				const newParentRot = ResolveObjRotInWorld(obj) ?? 0;
				for (const [i, copiedObject] of copiedObjects.entries()) {
					const copiedObject_worldPos = copiedObjects_worldPositions[i];
					const worldOffsetFromNewParent = copiedObject_worldPos.Minus(obj_posWorld).ToVec2();
					const newLocalPos = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(worldOffsetFromNewParent, newParentRot).ToVec3(0);
					await new UpdateMapObject({id: copiedObject.id, updates: {
						parent: obj.id,
						position: newLocalPos, // keep world-pos after parent change
					}}).RunOnServer();
					ACTSetCopiedData(CopiedData.none());
				}
			} else {
				const newIDs = [] as string[];
				const setSelection = ()=>RunInAction_Set(()=>{
					mapState.selectedObjects = newIDs;
					mapState.activeObject = newIDs.LastOrX();
				});

			const newParentRot = ResolveObjRotInWorld(obj) ?? 0;
				for (const [i, copiedObject] of copiedObjects.entries()) {
					const copiedObject_worldPos = copiedObjects_worldPositions[i];
					const worldOffsetFromNewParent = copiedObject_worldPos.Minus(obj_posWorld).ToVec2();
					const newLocalPos = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(worldOffsetFromNewParent, newParentRot).ToVec3(0);
					const newObj = Clone(copiedObject!);
					newObj.id = null;
					//newObj.name = `${copiedObject!.name} (clone)`;
					newObj.parent = obj.id;
					newObj.position = newLocalPos; // keep world-pos after parent change
					const {id: newObjId} = await new AddMapObject({entry: newObj}).RunOnServer();
					newIDs.push(newObjId);
					setSelection();
				}
			}
		}}/>
		<VMenuItem text="Duplicate" style={headerStyle} onClick={async()=>{
			const newObj = Clone(obj) as MapObject;
			//newObj.name = obj.name == "@" ? "@" : `${obj.name} (clone)`; // if name is a special/behavior-affecting name, keep it as is
			newObj.name = FinalizeObjNameForCloning(newObj.name);
			const {id: newObjId} = await new AddMapObject({entry: newObj}).RunOnServer();
			RunInAction_Set(()=>{
				mapState.selectedObjects = [newObjId];
				mapState.activeObject = newObjId;
			});
		}}/>
		{/*<VMenuItem text={mapState.referenceObject == obj.id ? "Unset as ref" : "Set as ref"} style={headerStyle} onClick={()=>{
			RunInAction("ObjectEntryUI_Menu.setReferenceObject", ()=>{
				if (mapState.referenceObject == obj.id) mapState.referenceObject = null;
				else mapState.referenceObject = obj.id;
			});
		}}/>*/}
		<VMenuItem text="Export subtree" style={headerStyle} onClick={()=>{
			ShowSubtreeExportDialog(obj, map.id);
		}}/>
		<VMenuItem text="Focus (map)" style={headerStyle} onClick={()=>{
			if (MapSurface_current == null) return;
			MapSurface_current.FocusObject(obj);
		}}/>
		<VMenuItem text="Reveal (outliner)" style={headerStyle} onClick={()=>{
			RunInAction("ObjectEntryUI_Menu.reveal", ()=>{
				for (const ancestor of objAncestors) {
					mapState.expandedObjects[ancestor.id] = true;
				}
			});
		}}/>
		<VMenuItem text="Delete" style={headerStyle} onClick={()=>{
			ShowMessageBox({
				title: "Delete object", cancelButton: true,
				message: `Delete the object "${obj.name}"?`,
				onOK: async()=>{
					await new DeleteMapObject({id: obj.id}).RunOnServer();
					if (mapState.activeObject == obj.id) {
						RunInAction("ObjectEntryUI_Menu.onDelete", ()=>{
							mapState.activeObject = null;
							mapState.selectedObjects = mapState.selectedObjects.filter(a=>a != obj.id);
						});
					}
				},
			});
		}}/>
	</>;
});

export function FinalizeObjNameForCloning(name: string) {
	return name.replaceAll(/ *<.*?>/g, ""); // remove angle-bracketed sections from name when cloning
}